import { formatarCNPJ } from '@spark/utils/formatters';

export const formatarDataComHora = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export const normalizeFilterValue = (value) => {
  if (Array.isArray(value)) {
    return value
      .map(item => normalizeFilterValue(item))
      .filter(item => item !== undefined && item !== null && item !== '');
  }

  if (value && typeof value === 'object' && 'value' in value) {
    return value.value;
  }

  return value;
};

export const normalizeDescargaFilters = (filters) => Object.fromEntries(
  Object.entries(filters || {})
    .map(([key, value]) => [key, normalizeFilterValue(value)])
    .filter(([, value]) => value !== undefined && value !== null && value !== '')
);

const fallback = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  return value;
};

// Sem chegada o backend devolve 0 ('000:00:00'): nada a exibir.
const formatTempoForaJanela = (value) => {
  if (value === null || value === undefined || value === '') return '-';

  const text = String(value).trim();
  return /^0+:00:00$/.test(text) ? '-' : text;
};

const formatCnpjOrFallback = (value) => formatarCNPJ(String(value || '')) || '-';
const onlyDigits = (value) => String(value || '').replace(/\D/g, '');

// String para que 0 palete nao seja tratado como vazio pelo renderText da tabela.
const formatQuantidadeOrFallback = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  return String(value);
};

const formatKmOrFallback = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return '-';

  return `${numericValue.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })} KM`;
};

export const pickFirstValue = (source, keys) => {
  for (const key of keys) {
    const value = source?.[key];
    if (value !== undefined && value !== null && value !== '') return value;
  }

  return null;
};

export const isDescargaManual = (item) => {
  const origem = pickFirstValue(item, ['origemRegistro', 'OrigemRegistro']);
  if (String(origem || '').toUpperCase() === 'MANUAL') return true;

  const manualId = pickFirstValue(item, ['descargaManualId', 'DescargaManualId']);
  return manualId !== null && manualId !== undefined && manualId !== '';
};

export const mapControleDescarga = (item, index) => {
  const manual = isDescargaManual(item);
  const descargaManualId = pickFirstValue(item, ['descargaManualId', 'DescargaManualId']);
  const origemRegistro = manual ? 'MANUAL' : (pickFirstValue(item, ['origemRegistro', 'OrigemRegistro']) || 'TMS');
  const stableId = manual
    ? `manual-${descargaManualId}`
    : (item.codigoCarga || item.codigoPedido || `${item.numeroCarga || 'carga'}-${item.protocoloCarga || 'protocolo'}-${index}`);

  return {
  id: stableId,
  raw: item,
  origemRegistro,
  descargaManualId,
  isDescargaManual: manual,
  pedCodigo: pickFirstValue(item, ['pedCodigo', 'PedCodigo', 'codigoPedido', 'CodigoPedido']),
  cliCodigo: onlyDigits(pickFirstValue(item, ['cliCodigo', 'CliCodigo', 'cnpjDestinatario', 'CNPJDestinatario'])),
  carCodigo: pickFirstValue(item, ['carCodigo', 'CarCodigo', 'codigoCarga', 'CodigoCarga']),
  ativo: pickFirstValue(item, ['ativo', 'Ativo']),
  numeroCarga: fallback(item.numeroCarga),
  cargasCTE: fallback(pickFirstValue(item, ['cargasCTE', 'cargasCte', 'cargascte', 'CargasCTE', 'CargasCte', 'CARGAS_CTE'])),
  protocoloCarga: fallback(item.protocoloCarga),
  codigoCarga: fallback(item.codigoCarga),
  situacaoCarga: fallback(item.situacaoCarga),
  descricaoTipoOperacao: fallback(pickFirstValue(item, ['descricaoTipoOperacao', 'DescricaoTipoOperacao'])),
  numeroPedido: fallback(item.numeroPedido),
  protocoloPedido: fallback(item.protocoloPedido),
  codigoPedido: fallback(item.codigoPedido),
  dataAgendamentoPedido: formatarDataComHora(item.dataAgendamentoPedido),
  quantidadePaletes: formatQuantidadeOrFallback(pickFirstValue(item, ['quantidadePaletes', 'QuantidadePaletes'])),
  placaVeiculo: fallback(item.placaVeiculo),
  numeroFrota: fallback(item.numeroFrota),
  tipoVeiculo: fallback(pickFirstValue(item, ['tipoVeiculo', 'TipoVeiculo'])),
  descricaoModeloVeicular: fallback(item.descricaoModeloVeicular),
  nomeRemetente: fallback(item.nomeRemetente),
  cnpjRemetente: formatCnpjOrFallback(item.cnpjRemetente),
  nomeDestinatario: fallback(item.nomeDestinatario),
  cnpjDestinatario: formatCnpjOrFallback(item.cnpjDestinatario),
  loja: fallback(item.loja),
  lojaRemetente: fallback(pickFirstValue(item, ['lojaRemetente', 'LojaRemetente'])),
  cidade: fallback(pickFirstValue(item, ['cidadeDestinatario', 'CidadeDestinatario', 'cidade', 'Cidade', 'nomeCidade', 'NomeCidade'])),
  ufDestinatario: fallback(pickFirstValue(item, ['ufDestinatario', 'UFDestinatario'])),
  nomeTomador: fallback(item.nomeTomador),
  cnpjTomador: formatCnpjOrFallback(item.cnpjTomador),
  placasReboque: fallback(item.placasReboque),
  frotasReboque: fallback(item.frotasReboque),
  modelosReboque: fallback(item.modelosReboque),
  dataCriacaoPedido: formatarDataComHora(item.dataCriacaoPedido),
  dataCriacaoCarga: formatarDataComHora(item.dataCriacaoCarga),
  dataCarregamentoPedido: formatarDataComHora(item.dataCarregamentoPedido),
  infoMotorista: fallback(item.infoMotorista),
  nomeResponsavel: fallback(item.nomeResponsavel),
  dataAgendamentoDescarga: formatarDataComHora(item.dataAgendamentoDescarga),
  dataAgendamentoDescargaOriginal: item.dataAgendamentoDescarga || null,
  chegadaDescarga: formatarDataComHora(item.chegadaDescarga),
  chegadaDescargaOriginal: item.chegadaDescarga || null,
  saidaDescarga: formatarDataComHora(item.saidaDescarga),
  saidaDescargaOriginal: item.saidaDescarga || null,
  tempoEsperaDescarga: fallback(pickFirstValue(item, ['tempoEsperaDescarga', 'TempoEsperaDescarga'])),
  tempoAtrasado: fallback(pickFirstValue(item, ['tempoAtrasado', 'TempoAtrasado'])),
  dataInicialEsperaDescarga: pickFirstValue(item, ['dataInicialEsperaDescarga', 'DataInicialEsperaDescarga']),
  inicioContagem: formatarDataComHora(pickFirstValue(item, ['dataInicialEsperaDescarga', 'DataInicialEsperaDescarga'])),
  tempoForaJanela: formatTempoForaJanela(pickFirstValue(item, ['tempoForaJanela', 'TempoForaJanela'])),
  tempoForaJanelaSegundos: pickFirstValue(item, ['tempoForaJanelaSegundos', 'TempoForaJanelaSegundos']),
  janelaDescargaNome: pickFirstValue(item, ['janelaDescargaNome', 'JanelaDescargaNome']),
  janelaAplicada: fallback(pickFirstValue(item, ['janelaDescargaNome', 'JanelaDescargaNome'])),
  chegouForaJanela: pickFirstValue(item, ['chegouForaJanela', 'ChegouForaJanela']),
  observacoesDescarga: fallback(item.observacoesDescarga),
  observacoesDescargaOriginal: item.observacoesDescarga || '',
  descricaoPosicaoAngellira: fallback(item.descricaoPosicaoAngellira),
  codigoViagemAngellira: fallback(item.codigoViagemAngellira),
  statusViagemAngellira: fallback(pickFirstValue(item, ['statusViagemAngellira', 'StatusViagemAngellira'])),
  clienteDistanciaAngellira: formatKmOrFallback(pickFirstValue(item, ['clienteDistanciaAngellira', 'ClienteDistanciaAngellira'])),
  clienteTempoPrevChegadaAngellira: fallback(pickFirstValue(item, ['clienteTempoPrevChegadaAngellira', 'ClienteTempoPrevChegadaAngellira']))
  };
};
